// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import { weekDayStartToNumber } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router';
import { DayCell } from '../components/calendar/DayCell';
import TitleCard from '../components/common/card/TitleCard';
import InternalDatePicker from '../components/common/date/InternalDatePicker';
import ErrorView from '../components/common/error/ErrorView';
import HarvestTable from '../components/harvest/HarvestTable';
import { groupPlantingsByTime } from '../components/harvest/groupPlantingsByTime';
import OrderTable from '../components/order/OrderTable';
import TaskGroupTable from '../components/task/TaskGroupTable';
import { groupTasksWithStatus } from '../components/task/groupTasks';
import { useUserContext } from '../context/UserContext';
import { useDateTransition } from '../hooks/useDateTransition';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, IconButton, Popover, Typography } from '../lib/base';
import { ArrowBackIosNewIcon, ArrowForwardIosIcon, SettingsIcon } from '../lib/icons';
import CardView from '../lib/layout/CardView';
import type { CalendarEventsQuery, CalendarEventsQueryVariables } from '../types/__generated__/graphql';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// TODO: this could be an attack vector if
// someone can craft a query that returns all the data
const CALENDAR_QUERY = gql`
  query CalendarEvents($start: String!, $end: String!) {
    orders(filters: [{ name: "order.order_fulfillment_time", operator: ">=", value: $start }, { name: "order.order_fulfillment_time", operator: "<=", value: $end }]) {
      edges {
        node {
          id
          customer
          order_fulfillment_time
          order_status
        }
      }
    }
    plantings(filters: [{ name: "planting.planting_time", operator: ">=", value: $start }, { name: "planting.planting_time", operator: "<=", value: $end }]) {
      edges {
        node {
          id
          planting_time
          planting_status
          trays_rounded
          crop {
            id
            crop_name
            status
          }
          order_id
          tasks {
            count
          }
        }
      }
    }
    tasks(filters: [{ name: "task.task_time", operator: ">=", value: $start }, { name: "task.task_time", operator: "<=", value: $end }]) {
      edges {
        node {
          id
          task_time
          task_name
          task_status
          crop {
            id
            crop_name
            status
          }
          planting {
            id
            order_id
            trays_rounded
          }
        }
      }
    }
  }
`;

type OrderData = NonNullable<CalendarEventsQuery['orders']['edges'][number]['node']>;
type PlantingData = NonNullable<CalendarEventsQuery['plantings']['edges'][number]['node']>;
type TaskData = NonNullable<CalendarEventsQuery['tasks']['edges'][number]['node']>;

const YEAR_PARAM = 'year';
const MONTH_PARAM = 'month';
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const keyDataDayFormat = 'YYYY-MM-DD';

export interface CalendarSettings {
  readonly showCompletedTasks: boolean;
  readonly showNonProductionTasks: boolean;
  readonly showProductionTasks: boolean;
  // readonly groupTasksRegardlessOfCrop: boolean;
  // readonly showOrders: boolean;
}

const MonthViewCalendar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { timezone, weekStartPreference } = useUserContext();
  const firstDayOfWeekNum = weekDayStartToNumber(weekStartPreference);

  // Get year and month from URL params
  const now = dayjs().tz(timezone);
  const yearParam = searchParams.get(YEAR_PARAM);
  const year = yearParam == null ? now.year() : Number.parseInt(yearParam) || now.year();
  const monthParam = searchParams.get(MONTH_PARAM);
  let month = now.month();
  if (monthParam != null) {
    month = Number.parseInt(monthParam);
    if (Number.isNaN(month) || month < 0 || month > 11) {
      month = now.month();
    }
  }

  const [currentDate, setCurrentDate] = React.useState(() => dayjs().tz(timezone).year(year).month(month));
  const prevMonth = React.useMemo(() => currentDate.subtract(1, 'month'), [currentDate]);
  const nextMonth = React.useMemo(() => currentDate.add(1, 'month'), [currentDate]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [settings, setSettings] = React.useState<CalendarSettings>({
    showCompletedTasks: true,
    showNonProductionTasks: true,
    showProductionTasks: true,
    // groupTasksRegardlessOfCrop: true,
    // showOrders: false,
  });

  const handleSettingsClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleSettingsClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const startDay = React.useMemo(() => {
    const start = currentDate.startOf('month');
    const dayOfWeek = start.day();
    const diff = (dayOfWeek - firstDayOfWeekNum + 7) % 7;

    // Setting hour to 12 is necessary to prevent time changes from affecting things
    return start.subtract(diff, 'day').hour(12);
  }, [currentDate, firstDayOfWeekNum]);
  const endDay = React.useMemo(() => {
    const end = currentDate.endOf('month');
    const dayOfWeek = end.day();
    const diff = (6 + firstDayOfWeekNum - dayOfWeek + 7) % 7;

    return end.add(diff, 'day');
  }, [currentDate, firstDayOfWeekNum]);

  const days = React.useMemo(
    () => Array.from({ length: endDay.diff(startDay, 'day') + 1 }).map((_, index) => startDay.add(index, 'day')),
    [startDay, endDay],
  );
  const rows = React.useMemo(() => days.length / 7, [days]);

  const { loading, error, data } = useQuery<CalendarEventsQuery, CalendarEventsQueryVariables>(CALENDAR_QUERY, {
    variables: {
      start: startDay.unix().toString(),
      end: endDay.unix().toString(),
    },
  });

  const onTransition = React.useCallback(
    (newDate: dayjs.Dayjs) => {
      setCurrentDate(newDate);
      // Always clear params since newDate is always current date
      setSearchParams();
    },
    [setSearchParams],
  );
  useDateTransition({
    timezone,
    onTransition,
  });

  // If newDate has same year and month as now, then remove URL params
  const handlePrevMonth = React.useCallback(() => {
    setCurrentDate((prevDate) => {
      const newDate = prevDate.subtract(1, 'month');
      if (newDate.year() === now.year() && newDate.month() === now.month()) {
        setSearchParams();
      } else {
        setSearchParams({ [YEAR_PARAM]: newDate.year().toString(), [MONTH_PARAM]: newDate.month().toString() });
      }
      return newDate;
    });
  }, [setSearchParams, now]);
  const handleNextMonth = React.useCallback(() => {
    setCurrentDate((prevDate) => {
      const newDate = prevDate.add(1, 'month');
      if (newDate.year() === now.year() && newDate.month() === now.month()) {
        setSearchParams();
      } else {
        setSearchParams({ [YEAR_PARAM]: newDate.year().toString(), [MONTH_PARAM]: newDate.month().toString() });
      }
      return newDate;
    });
  }, [setSearchParams, now]);
  const handleTodayClick = React.useCallback(() => {
    const today = dayjs().tz(timezone);
    setCurrentDate(today);
    setSearchParams();
  }, [timezone, setSearchParams]);

  const onClickDay = React.useCallback(
    (day: dayjs.Dayjs, isSelectedDay: boolean, shouldSetParams: boolean) => {
      if (isSelectedDay) {
        return;
      }
      setCurrentDate(day);
      // If we click a day outside current month, change the month (and year) and set the selected date
      if (shouldSetParams) {
        setSearchParams({ [YEAR_PARAM]: day.year().toString(), [MONTH_PARAM]: day.month().toString() });
      }
    },
    [setSearchParams],
  );
  const eventsData = React.useMemo(() => {
    if (!data) {
      return {
        tasksPerDay: new Map<string, TaskData[]>(),
        plantingsPerDay: new Map<string, PlantingData[]>(),
        ordersPerDay: new Map<string, OrderData[]>(),
      };
    }

    const tasksPerDay = new Map<string, TaskData[]>();
    const plantingsPerDay = new Map<string, PlantingData[]>();
    const ordersPerDay = new Map<string, OrderData[]>();

    for (const edge of data.tasks.edges) {
      const key = dayjs.unix(edge.node.task_time).tz(timezone).format(keyDataDayFormat);
      if (!tasksPerDay.has(key)) {
        tasksPerDay.set(key, []);
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      tasksPerDay.get(key)!.push(edge.node);
    }

    for (const edge of data.plantings.edges) {
      const key = dayjs.unix(edge.node.planting_time).tz(timezone).format(keyDataDayFormat);
      if (!plantingsPerDay.has(key)) {
        plantingsPerDay.set(key, []);
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      plantingsPerDay.get(key)!.push(edge.node);
    }

    for (const edge of data.orders.edges) {
      const key = dayjs.unix(edge.node.order_fulfillment_time).tz(timezone).format(keyDataDayFormat);
      if (!ordersPerDay.has(key)) {
        ordersPerDay.set(key, []);
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      ordersPerDay.get(key)!.push(edge.node);
    }

    return { tasksPerDay, plantingsPerDay, ordersPerDay };
  }, [data, timezone]);

  const monthYearLabel = React.useMemo(() => currentDate.format('MMMM YYYY'), [currentDate]);

  const selectedDayContent = React.useMemo(() => {
    if (currentDate == null) {
      return null;
    }
    const selectedDayLabel = currentDate.format('ddd, MMM D');
    const selectedDayKey = currentDate.format(keyDataDayFormat);
    const selectedDayTaskGroups = [
      ...groupTasksWithStatus<TaskData>(eventsData.tasksPerDay.get(selectedDayKey) ?? []).values(),
    ];
    const selectedDayGroupedHarvests = [
      ...groupPlantingsByTime<PlantingData>(eventsData.plantingsPerDay.get(selectedDayKey) ?? []).values(),
    ];
    const selectedDayOrders = eventsData.ordersPerDay.get(selectedDayKey) ?? [];

    return (
      <Grid container sx={{ mt: 2 }} size={{ xs: 12 }} rowGap={2}>
        {selectedDayTaskGroups.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <TitleCard
              title={`Tasks - ${selectedDayLabel}`}
              rightElement={<div />}
              content={
                <TaskGroupTable showActionButtons={true} taskGroups={selectedDayTaskGroups.map(({ tasks }) => tasks)} />
              }
            />
          </Grid>
        )}
        {selectedDayGroupedHarvests.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <TitleCard
              title={`Harvests - ${selectedDayLabel}`}
              rightElement={<div />}
              content={<HarvestTable harvestGroups={selectedDayGroupedHarvests.map((g) => g.plantings)} />}
            />
          </Grid>
        )}
        {selectedDayOrders.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <TitleCard
              title={`Orders - ${selectedDayLabel}`}
              rightElement={<div />}
              content={<OrderTable orders={selectedDayOrders} />}
            />
          </Grid>
        )}
      </Grid>
    );
  }, [currentDate, eventsData]);

  const weekDays = React.useMemo(
    () => [...DAYS_OF_WEEK.slice(firstDayOfWeekNum), ...DAYS_OF_WEEK.slice(0, firstDayOfWeekNum)],
    [firstDayOfWeekNum],
  );
  const weekDaysLabels = React.useMemo(
    () =>
      weekDays.map((day) => (
        <Box key={day} sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
          <Typography variant="button" display="block" align="center">
            {day}
          </Typography>
        </Box>
      )),
    [weekDays],
  );

  if (error) {
    return <ErrorView error={error} />;
  }

  return (
    <CardView title={monthYearLabel} extra={selectedDayContent ?? undefined}>
      <Helmet>
        <title>{monthYearLabel}</title>
      </Helmet>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2}
          sx={{ padding: 1, borderBottom: '1px solid', borderBottomColor: 'divider' }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton aria-label="calendar-settings" size="small" onClick={handleSettingsClick}>
              <SettingsIcon />
            </IconButton>
            <InternalDatePicker
              value={currentDate}
              onChange={(newDate) => {
                if (newDate) {
                  const date = dayjs(newDate).tz(timezone);
                  setCurrentDate(date);
                  if (date.year() === now.year() && date.month() === now.month()) {
                    setSearchParams();
                  } else {
                    setSearchParams({
                      [YEAR_PARAM]: date.year().toString(),
                      [MONTH_PARAM]: date.month().toString(),
                    });
                  }
                }
              }}
              noClear
              textFieldProps={{
                size: 'small',
                sx: { maxWidth: '160px' },
              }}
            />
          </Box>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.showCompletedTasks}
                      onChange={(e) => setSettings({ ...settings, showCompletedTasks: e.target.checked })}
                    />
                  }
                  label="Show Completed Tasks"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.showNonProductionTasks}
                      onChange={(e) => setSettings({ ...settings, showNonProductionTasks: e.target.checked })}
                    />
                  }
                  label="Show Non-Production Tasks"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.showProductionTasks}
                      onChange={(e) => setSettings({ ...settings, showProductionTasks: e.target.checked })}
                    />
                  }
                  label="Show Production Tasks"
                />
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.groupTasksRegardlessOfCrop}
                      onChange={(e) => setSettings({ ...settings, groupTasksRegardlessOfCrop: e.target.checked })}
                    />
                  }
                  label="Show Tasks by Crop"
                /> */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.showOrders}
                      onChange={(e) => setSettings({ ...settings, showOrders: e.target.checked })}
                    />
                  }
                  label="Show Orders"
                /> */}
              </FormGroup>
            </Box>
          </Popover>
          <Box display="flex" alignItems="center" gap={1}>
            <Button variant="outlined" onClick={handlePrevMonth} startIcon={<ArrowBackIosNewIcon />}>
              Prev
            </Button>
            <Button variant="outlined" onClick={handleTodayClick}>
              Today
            </Button>
            <Button variant="outlined" onClick={handleNextMonth} endIcon={<ArrowForwardIosIcon />}>
              Next
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0,
            containerType: 'inline-size',
            containerName: 'month-calendar',
          }}
        >
          {weekDaysLabels}
          {days.map((day, idx) => {
            const currentRow = Math.floor(idx / 7);
            const isLastRow = currentRow === rows - 1;
            const isSelectedDay = currentDate != null && day.isSame(currentDate, 'day');
            const dayKey = day.format(keyDataDayFormat);
            const taskEvents = eventsData.tasksPerDay.get(dayKey) ?? [];
            const isPrevMonth = day.month() === prevMonth.month();
            const isNextMonth = day.month() === nextMonth.month();
            const shouldSetParams = day.month() !== currentDate.month();

            return (
              <DayCell
                key={day.unix()}
                day={day}
                index={idx}
                isLastRow={isLastRow}
                onClickDay={onClickDay}
                isSelectedDay={isSelectedDay}
                showSkeletons={loading}
                taskData={taskEvents}
                rows={rows}
                settings={settings}
                shouldSetParams={shouldSetParams}
                isPrevMonth={isPrevMonth}
                isNextMonth={isNextMonth}
              />
            );
          })}
        </Box>
      </Box>
    </CardView>
  );
};

export default React.memo(MonthViewCalendar);
